import { getAccountingPurchasesAndSales } from 'lib/api/accounting';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import GlobalSearcher, {
  Filter,
} from '../../../../../../lib/common/GlobalSearcher';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import { showAlert } from '../../../../../../lib/functions/utils';
import { PurchasesAndSalesDocument } from '../../types';
import AssignmentDialog from './components/AssignmentDialog';

const AccountingPurchansesAndSales: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [purchasesAndSales, setPurchasesAndSales] = useState<
    PurchasesAndSalesDocument[]
  >([]);
  const [searchFolio, setSearchFolio] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [assignationModuleOpen, setAssignationModuleOpen] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<
    PurchasesAndSalesDocument[]
  >([]);
  const [type, setType] = useState<'purchase' | 'sale'>('purchase');
  const [documentSubType, setDocumentSubType] = useState<string>('all');
  const [showingType, setShowingType] = useState<'purchase' | 'sale'>(
    'purchase',
  );
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);

  const PAGE_SIZE = 10;
  const DEFAULT_PAGE = 1;
  const [pagination, setPagination] = useState({
    page: DEFAULT_PAGE,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const headers: { key: string; label: string; type?: string }[] = [
    {
      key: 'centralizationStatus',
      label: 'Estado centralización',
      type: 'status-chip',
    },
    { key: 'folio', label: 'Folio' },
    { key: 'subType', label: 'Código' },
    { key: 'documentType', label: 'Tipo' },
    ...(showingType === 'purchase'
      ? [{ key: 'businessName', label: 'Proveedor' }]
      : [{ key: 'stakeholderName', label: 'Deudor' }]),
    ...(showingType === 'purchase'
      ? [{ key: 'businessIdentifier', label: 'Rut' }]
      : [{ key: 'stakeholderIdentifier', label: 'Rut' }]),
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'issuedDate', label: 'Emisión', type: 'eight-days-date' },
    { key: 'receiptDate', label: 'Recepción', type: 'date' },
    { key: 'confirmDate', label: 'Acuse', type: 'date' },
    { key: 'cancelDate', label: 'Cancelación', type: 'date' },
    { key: 'exentAmount', label: 'Monto exento', type: 'money' },
    { key: 'retentionAmount', label: 'Monto retención', type: 'money' },
    { key: 'netAmount', label: 'Monto neto', type: 'money' },
    { key: 'ivaAmount', label: 'Monto IVA', type: 'money' },
    {
      key: 'ivaNotRecoverableAmount',
      label: 'Monto IVA no recuperable',
      type: 'money',
    },
    {
      key: 'ivaRecoverableAmount',
      label: 'Monto IVA recuperable',
      type: 'money',
    },
    { key: 'financialNode', label: 'Cuenta' },
    { key: 'financialNodeAccountCode', label: 'Código de cuenta' },
    { key: 'costCenterNode', label: 'Centro de costo' },
    ...(type === 'purchase' ? [{ key: 'entryId', label: 'ID Asiento' }] : []),
  ];

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (name) query += `name=${name}&`;
    if (searchFolio) query += `folio=${searchFolio}&`;
    if (documentSubType) query += `documentSubType=${documentSubType}&`;
    if (type) query += `type=${type}&`;
    if (identifier) query += `identifier=${identifier}&`;
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    return query;
  };

  const fetchPayments = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAccountingPurchasesAndSales(dispatch, query);
      setPurchasesAndSales(response.data);
      setShowingType(type);
      setPagination(response.pagination);
      setLoading(false);
      return response;
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener los pagos');
    }
  };

  const setUp = async () => {
    await fetchPayments({ page: 1, pageSize: 10 });
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleAssignationModule = () => {
    // Filtrar los documentos seleccionados
    const selectedDocs = purchasesAndSales.filter((doc) =>
      selectedIds.includes(doc.id),
    );
    setSelectedDocuments(selectedDocs);
    setAssignationModuleOpen(true);
  };

  const filters: Filter[] = [
    {
      type: 'select',
      label: 'Tipo de documento',
      value: documentSubType,
      setter: setDocumentSubType,
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Factura Electronica Afecta', value: '33' },
        { label: 'Factura Electronica Exenta', value: '34' },
        { label: 'Nota de credito', value: '61' },
      ],
    },
    {
      type: 'select',
      label: 'Tipos',
      value: type,
      setter: setType,
      options: [
        { label: 'Compra', value: 'purchase' },
        { label: 'Venta', value: 'sale' },
      ],
    },
    {
      type: 'text',
      label: `${type === 'purchase' ? 'Proveedor' : 'Deudor'}`,
      value: identifier,
      setter: setIdentifier,
    },
    {
      type: 'autocomplete',
      label: `${type === 'purchase' ? 'Nombre proveedor' : 'Nombre deudor'}`,
      key: 'businessName',
      value: name,
      setter: setName,
    },
    {
      type: 'text',
      label: 'Folio',
      value: searchFolio,
      setter: setSearchFolio,
    },
    {
      type: 'date',
      label: 'Inicio Periodo',
      dateKey: 'startDate',
      value: startDate,
      setter: setStartDate,
    },
    {
      type: 'date',
      label: 'Fin Periodo',
      dateKey: 'endDate',
      value: endDate,
      setter: setEndDate,
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'secondary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => fetchPayments({ page: 1, pageSize: 10 })}
        />
      ),
    },
    {
      name: 'Asignar',
      color: 'primary',
      action: handleAssignationModule,
      disabled: selectedIds.length === 0,
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title={`${
          showingType === 'purchase'
            ? 'Acá encontrarás todos los documentos de compra'
            : 'Acá encontrarás todos los documentos de venta'
        }`}
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        data={purchasesAndSales}
        dataHeaders={headers}
        selected={selectedIds}
        setSelected={setSelectedIds}
        canSelectAll={true}
        finalSumRow={true}
        exportToFile="xlsx"
        pagination={pagination}
        setPagination={setPagination}
        setData={setPurchasesAndSales}
        searchData={fetchPayments}
        setLoading={setLoading}
        exportComplete
      />

      {assignationModuleOpen && selectedDocuments && (
        <AssignmentDialog
          open={assignationModuleOpen}
          onClose={() => setAssignationModuleOpen(false)}
          documents={selectedDocuments}
          onAssignmentComplete={() => {
            void setUp();
            setSelectedIds([]);
          }}
        />
      )}
    </div>
  );
};

export default AccountingPurchansesAndSales;
